'use client';

/**
 * AppMenu, burger meni z vsemi (pod)stranmi aplikacije (seja 332).
 *
 * Strani se je nabralo preveč za tri gumbe v glavi: skupni vstopi (Pogovor,
 * Pravni red, Nomotehnika, Geslovnik US) + sklop Atlas (skrbnik ali raziskovalec) in
 * Nadzor (samo skrbnik). Omejene postavke
 * so vidne le, ko session nosi isAdmin / isResearcher (server jih tako ali tako varuje;
 * meni je samo napotitev, ne varnostna meja). Aktivna stran je označena.
 */
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import {
  Menu,
  MessageSquare,
  Waypoints,
  ScrollText,
  Map,
  Network,
  AlertTriangle,
  Scale,
  Gauge,
  Activity,
  Search,
  Terminal,
  BookMarked,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Entry {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const COMMON: Entry[] = [
  { href: '/chat', label: 'Pogovor', icon: MessageSquare },
  { href: '/pravni-red', label: 'Pravni red', icon: Waypoints },
  { href: '/nomotehnika', label: 'Nomotehnika', icon: ScrollText },
  // Geslovnik US (sklop C, seja 374): lastno mesto, ne pod Nomotehniko (uporabnikova zahteva)
  { href: '/usrs', label: 'Geslovnik US', icon: BookMarked },
];

const ATLAS: Entry[] = [
  { href: '/atlas', label: 'Atlas: Pravni red', icon: Waypoints },
  { href: '/atlas/zemljevid', label: 'Zemljevid področij', icon: Map },
  { href: '/atlas/raziskovalec', label: 'Raziskovalec grafa', icon: Network },
  { href: '/atlas/anomalije', label: 'Anomalije', icon: AlertTriangle },
  { href: '/atlas/sodniki', label: 'Sodniki US', icon: Scale },
  { href: '/atlas/konzola', label: 'Konzola (SQL / DSL)', icon: Terminal },
];

const NADZOR: Entry[] = [
  { href: '/nadzor', label: 'Nadzorna plošča', icon: Gauge },
  { href: '/nadzor/status', label: 'Stanje sistema', icon: Activity },
  { href: '/nadzor/os/app/home', label: 'OpenSearch Dashboards', icon: Search },
];

export function AppMenu() {
  const pathname = usePathname();
  const { data: session } = useSession();
  const admin = session?.user?.isAdmin === true;
  // Raziskovalec (RESEARCH_EMAILS, seja 401) vidi Atlas, ne pa Nadzora.
  const research = admin || session?.user?.isResearcher === true;

  const item = (e: Entry) => {
    const active = pathname === e.href || (e.href !== '/chat' && pathname?.startsWith(e.href + '/'));
    const Icon = e.icon;
    return (
      <DropdownMenuItem key={e.href} asChild className={`cursor-pointer ${active ? 'bg-secondary font-medium' : ''}`}>
        <Link href={e.href}>
          <Icon className="mr-2 h-4 w-4" />
          <span>{e.label}</span>
        </Link>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="px-2" title="Vse strani">
          <Menu className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60" align="end">
        {COMMON.map(item)}
        {research && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">Atlas</DropdownMenuLabel>
            {ATLAS.map(item)}
          </>
        )}
        {admin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">Nadzor</DropdownMenuLabel>
            {NADZOR.map(item)}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
